import { IRealTimeTranscriptionConnection } from '../interfaces/IRealTimeTranscriptionConnection'
import { ITranscription, ITranscriptionSegment } from "../interfaces/ITranscriptionManager";
import { VoiceSession, SpeechToTextClient, DictationSettingsManager } from '@ms/al-voice-client';
import { SpeechToTextProfile } from '@ms/al-voice-client/lib/VoiceSession';
import { SpeechToTextResultType } from '@ms/al-voice-client/lib/SpeechToTextClient';
import { ALFactoryGlobal, IAugLoopRuntimeManager } from '@augloop/office-online';
import { IOperation } from '@augloop/types-core';
import { secFrom100nsec, msFrom100nsec, GetNumericSpeakerIdFromSpeakerId } from '../Utils'
import { LogEvents } from '../../utils/LogEvents';
import { AugloopRealTimeTranscriptionConnectionKeys } from './telemetry/AugloopRealTimeTranscriptionConnectionKeys';
import { ResettableTimeout } from '../../utils/ResettableTimeout';
import { DictationEventsType, ISpeechSessionStarted } from '@augloop/types-voice';
import { RealTimeTranscriptionConnectionError, RealTimeTranscriptionConnectionErrors } from '../RealTimeTranscriptionConnectionErrors';
import { GetInitializeRuntimeManagerTask } from './Augloop';
import { IsDiarizationEnabled } from '../office-js/HostAppSettings';
import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { LogEvent } from "../TelemetryLogger/LogEvent";
import { LogError } from '../TelemetryLogger/Details/Logger';

const FD_TEXT_LANG_EN: string = 'en-US';
const FD_USE_AUTOPUNCTUATION_INTELLIGENT = 'Intelligent'; // Enable auto-punctuation.
const TRANSCRIPT_TEXT_LANG_EN: string = 'en';

DictationSettingsManager.dictationSettings = {
  dictationLanguage: FD_TEXT_LANG_EN,
  useAutoPunctuation: FD_USE_AUTOPUNCTUATION_INTELLIGENT,
  useCorrections: 'true'
};

export class AugloopRealTimeTranscriptionConnection implements IRealTimeTranscriptionConnection {
    private speechToTextClient: SpeechToTextClient;
    private voiceSession: VoiceSession;
    private transcription: ITranscription;
    private finalTrascriptTimeout: ResettableTimeout;
    private isFinalTranscriptAvailable: boolean = false;
    private onAudioBuffer: (buffer: AudioBuffer) => void;
    private isVoiceSessionActive: boolean;
    private offsetAdjustmentMs: number = 0;
    private speechSessionStartedTime: number | null = null;
    private lastPausedTime: number | null = null;
    private pauseAdjustmentMs: number = 0;
    private onError: (error: Error) => void;
    private inputFile: File | null = null; // Input file. If null, this is a microphone input session.
    private maxSpeakerId: number | null = null;
    private initPromise: Promise<void>;
    private onSendFileProgressUpdate: (lastTimestampReceived: number) => void | null;
    private onAudioDurationReceived: (audioDurationSeconds: number) => void | null;
    private maxProgressUpdateTimestamp: number = 0;

    // Keep a map of transcript segments indexed by adjusted offset (offset is a way to uniquely identify each result).
    // The map contains segments in the order we initially received them. When receiving NearRT results at the end of the session,
    // some of the entries may be overwritten by results with corrected speakerIDs.
    private transcriptSegmentsMap: Map<number, ITranscriptionSegment>;

    private fdSpeechClientObserver = {
      onSpeechSessionStarted: (speechSessionStarted: ISpeechSessionStarted) => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechSessionStarted);

          if (speechSessionStarted != null) {
            LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{key: AugloopRealTimeTranscriptionConnectionKeys.SpeechSessionId, value: speechSessionStarted.speechSessionId}]);
            console.log("Transcription speech session ID: ", speechSessionStarted.speechSessionId);
          }

          // It is possible for a cognitive services session to be restarted (and hence get reset speech event offsets).
          // If this happens, attempt to adjust the received offsets by the time difference between the initial onSpeechSessionStarted event and the latest one.
          // When a session is reset, we need to subtract the time the user was paused for from our offset adjustment, since we don't want that time to be included
          // in the transcript timestamps.
          if (this.speechSessionStartedTime != null) {
            this.offsetAdjustmentMs = Date.now() - this.speechSessionStartedTime - this.pauseAdjustmentMs;
            LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.OffsetAdjustmentMs, value: this.offsetAdjustmentMs}]);
          } else {
            this.speechSessionStartedTime = Date.now();
          }
        } catch (e) {
          this.onError(e);
        }
      },
      onFinalText: (text: Map<string, string>, properties: Map<string, string>) => this.OnFinalResult(text, properties),
      onFragmentText: (text: Map<string, string>, properties: Map<string, string>) => this.OnPartialResult(text, properties),
      onSpeechRecognitionStarted: () => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechRecognitionStarted);

          // If we were just unpaused, add the length of the pause to our pause adjustment so we can remove it if the session gets reset.
          if (this.lastPausedTime != null) {
            const pauseAdjustment = Date.now() - this.lastPausedTime;
            this.pauseAdjustmentMs += pauseAdjustment;
            this.lastPausedTime = null;
          } 
        } catch (e) {
          this.onError(e);
        }
      },
      onError: (error: string) => {
        try {
          // Ignore this deprectated error. It is now handled by catching a promise rejection.
          if (error == DictationEventsType.MicrophoneError)
            return;

          LogEvents.AugloopRealTimeTranscriptionConnection.AddData(
            [{ key: AugloopRealTimeTranscriptionConnectionKeys.OnError, value: 'IAugloopSpeechObserverDecorator.onError: ' + error }]);

          let e = new Error('IAugloopSpeechObserverDecorator.onError: ' + error);
          this.onError(e);
        } catch (e) {
          this.onError(e);
        }
      },
      onSpeechEnd: () => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechEnd);
        } catch (e) {
          this.onError(e);
        }
      }
    };

    constructor() {
      this.transcription = { transcriptSegments: [] };
      this.finalTrascriptTimeout = null;
      this.onError = (error: Error) => { throw error };
      this.onAudioBuffer = (_) => {};
      this.initPromise = this.PerformInitializeConnection();
      this.transcriptSegmentsMap = new Map();
    }

    // Initializes a real-time connection.
    private async PerformInitializeConnection(): Promise<void> {
      LogEvents.AugloopRealTimeTranscriptionConnection.Start([{ key: AugloopRealTimeTranscriptionConnectionKeys.IsDiarizationEnabled, value: IsDiarizationEnabled() }]);
      const alRuntimeManager = await this.EnsureAugloopRuntimeManager();
      await this.InitializeSpeechToTextClient(alRuntimeManager);

      // TODO: Switch non-diarization profile to Transcription.
      const speechProfile = IsDiarizationEnabled() ? SpeechToTextProfile.ConversationTranscription : SpeechToTextProfile.Dictation;

      // VoiceSession is responsible for sending voice data to augloop.
      this.voiceSession = new VoiceSession(alRuntimeManager, this.fdSpeechClientObserver, true /*useFrontdoorWorkflow*/, undefined /*commandTelemetry*/, undefined /*sessionTelemetry*/, false /*isVoiceInterfaceVersion2Enabled*/, speechProfile);
    }

    public SetAudioBufferCallback(onAudioBuffer: (buffer: AudioBuffer) => void): void {
      this.onAudioBuffer = onAudioBuffer;
    }

    public SetErrorCallback(onError: (error: Error) => void): void {
      this.onError = onError;
    }

    public async InitializeConnection(): Promise<void> {
      return this.initPromise;
    }

    // Starts capturing microphone input and sends the captured audio data through the real-time connection.
    public async StartMicrophone() {
      return this.ResumeConnection();
    }

    // Sends a file through the real-time connection.
    public async SendFile(file: File, onProgressUpdate: (lastTimestampReceived: number) => void, onAudioDurationReceived: (audioDurationSeconds: number) => void): Promise<void> {
      this.inputFile = file;
      this.onSendFileProgressUpdate = onProgressUpdate;
      this.onAudioDurationReceived = onAudioDurationReceived;
      return this.ResumeConnection();
    }

    // Starts sending input through the real-time connection.
    public async ResumeConnection(): Promise<void> {
      if (this.isVoiceSessionActive)
        return Promise.resolve();

      await this.initPromise;
      this.isVoiceSessionActive = true;

      if (this.inputFile != null) {
        return this.voiceSession.startVoiceSessionFromAudioFile(this.inputFile, true /*decodeAudio*/, (decodingTimeMs: number, audioDurationSeconds: number) => {
          LogEvents.AugloopRealTimeTranscriptionConnection.AddData([
            {key: AugloopRealTimeTranscriptionConnectionKeys.AudioDecodingTimeMs, value: decodingTimeMs}]);

            if (this.onAudioDurationReceived)
              this.onAudioDurationReceived(audioDurationSeconds);
        } /*onAudioDecoded*/);
      } else {
        try {
          await this.voiceSession.startVoiceSessionFromMicrophoneInput((buffer: AudioBuffer) => this.OnAudioBufferCallback(buffer) /*buffercallback*/);
        } catch (s) {
          if (s == 'Microphone error: Permission to use Microphone denied')
            throw new RealTimeTranscriptionConnectionError(RealTimeTranscriptionConnectionErrors.MicrophonePermissionError);
          else
            throw new Error('Unknown microphone error');
        }
      }
    }

    // Stops sending input through the real-time connection
    public async PauseConnection(): Promise<void> {
      if (!this.isVoiceSessionActive)
        return Promise.resolve();

      await this.initPromise;
      this.isVoiceSessionActive = false;
      await this.voiceSession.stopMicrophoneInputAsync();
      this.lastPausedTime = Date.now();
    }

    // Closes real-time connection
    public async CloseConnection(): Promise<void> {
      await this.initPromise;
      if (this.voiceSession) {
        this.voiceSession.endVoiceSession();
      }

      if (this.speechToTextClient) {
        await this.speechToTextClient.uninitializeAsync();
      }
      const endLogEvent = new LogEvent<AugloopRealTimeTranscriptionConnectionKeys>(LogEventNames.AugloopRealTimeTranscriptionConnectionEnd);
      endLogEvent.Start();
      const status: string = LogEvents.AugloopRealTimeTranscriptionConnection.End();
      endLogEvent.End([{key: AugloopRealTimeTranscriptionConnectionKeys.TelemetryDiagnostics, value: status}]);
    }

    // Returns the final transcript when it is available.
    public async EndSessionAndGetFinalTranscript(): Promise<ITranscription> {
      if (this.isFinalTranscriptAvailable)
        return this.transcription;

      // End the voice session so the end of audio signal is sent
      // and we receive the last final results.
      if (IsDiarizationEnabled() && this.voiceSession) {
        this.voiceSession.endVoiceSession();
        this.voiceSession = null;
      }

      return new Promise<ITranscription>((resolve) => {
        // Wait for 5 seconds since the last fragment/final result to make sure we got all transcript segments.
        // This is a hack. In the future, the service should give us a signal saying they're done.
        // We need this for transcription and not dictation because in transcription the user expects us
        // to transcribe everything they said, not just everything they said until they stopped recording.
        this.finalTrascriptTimeout = new ResettableTimeout(() => {
          this.isFinalTranscriptAvailable = true;
          this.transcription.speakerNames = new Array(this.maxSpeakerId != null ? this.maxSpeakerId + 1 : 0);
          LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.MaxSpeakerId, value: this.maxSpeakerId }]);

          // Dump transcriptSegmentsMap into the resulting transcript.
          // transcriptSegmentsMap is iterated in insertion order.
          let lastTimestamp = null;
          this.transcriptSegmentsMap.forEach((segment: ITranscriptionSegment) => {
            if (lastTimestamp != null && segment.start < lastTimestamp) {
              LogError({area: LogEventNames.AugloopRealTimeTranscriptionConnection, message: "Received segments out of order"});
            }

            lastTimestamp = segment.start;
            this.transcription.transcriptSegments.push(segment);
          });

          // There's an issue in CTS where speech is returning responses out of order. Order our transcript by timestamp.
          this.transcription.transcriptSegments.sort((segment1: ITranscriptionSegment, segment2: ITranscriptionSegment) => {
            return segment1.start - segment2.start;
          });

          resolve(this.transcription);
        }, 5000);
      })
    }

    private async EnsureAugloopRuntimeManager() {
      await GetInitializeRuntimeManagerTask();

      // A runtime manager represents a session in augloop, so we need to make sure we have one.
      // It exposes mechanisms to send and receive data from augloop in a session, so the
      // voice-related classes we depend on (VoiceSession, etc) need an instance of the manager
      // to communicate with AL.
      const alRuntimeManager = ALFactoryGlobal.getAugLoopRuntimeManager();

      if (alRuntimeManager == null)
        throw new Error('Augloop runtime manager is not initialized');

      return alRuntimeManager;
    }

    // Callback for final results
    private OnFinalResult(text: Map<string, string>, properties: Map<string, string>) {
      try {
        LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnFinalResult);
        LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Successfully received speech to text response", isFinalResponse: true });

        if (this.finalTrascriptTimeout != null) {
          this.finalTrascriptTimeout.Reset();
        }
  
        // Extract JsonResult from properties and parse it.
        const jsonResult = properties.get('JsonResult');
        const propertiesObj = JSON.parse(jsonResult);

        const adjustedOffsetMs = msFrom100nsec(propertiesObj.Offset) + this.offsetAdjustmentMs;
        const startTimestamp = secFrom100nsec(propertiesObj.Offset) + Math.floor(this.offsetAdjustmentMs / 1000);
        const endTimestamp = startTimestamp + secFrom100nsec(propertiesObj.Duration);
  
        const speakerId = GetNumericSpeakerIdFromSpeakerId(propertiesObj.SpeakerId);
  
        if (speakerId != null) {
          if (this.maxSpeakerId == null) {
            this.maxSpeakerId = speakerId;
          } else {
            this.maxSpeakerId = Math.max(this.maxSpeakerId, speakerId);
          }
        }
  
        const segment = {
          text: text.get(FD_TEXT_LANG_EN), language: TRANSCRIPT_TEXT_LANG_EN, start: startTimestamp, end: endTimestamp, speakerId
        };

        if (this.transcriptSegmentsMap.has(adjustedOffsetMs)) {
          LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.ReceivedNearRTResult, value: true }]);
        }

        this.transcriptSegmentsMap.set(adjustedOffsetMs, segment);
      } catch (e) {
        this.onError(e);
      }
    }

    // Callback for partial results
    private OnPartialResult(_text: Map<string, string>, properties: Map<string, string>) {
      try {
        const jsonResult = properties.get('JsonResult');
        const propertiesObj = JSON.parse(jsonResult);
        const startTimestamp = secFrom100nsec(propertiesObj.Offset) + Math.floor(this.offsetAdjustmentMs / 1000);
        const endTimestamp = startTimestamp + secFrom100nsec(propertiesObj.Duration);

        // Since we are using partial results, sometimes we receive results with an earlier offset.
        // Make sure we don't report anything less than the max we have reported so progress always moves forward.
        this.maxProgressUpdateTimestamp = Math.max(endTimestamp, this.maxProgressUpdateTimestamp);
  
        if (this.inputFile != null && this.onSendFileProgressUpdate != null) {
          this.onSendFileProgressUpdate(this.maxProgressUpdateTimestamp);
        }

        LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnPartialResult);
        if (this.finalTrascriptTimeout != null) {
          this.finalTrascriptTimeout.Reset();
        }
      } catch (e) {
        this.onError(e);
      }
    }

    // Initialize the speech to text client.
    private async InitializeSpeechToTextClient(alRuntimeManager: IAugLoopRuntimeManager) {
      // SpeechToTextClient subscribes to augloop speech-to-text events and exposes them to the caller.
      // In order to receive events from SpeechToTextClient, a VoiceSession needs to send voice data to augloop.
      this.speechToTextClient = new SpeechToTextClient(
        alRuntimeManager,
        this.fdSpeechClientObserver /*fdObserver*/,
        true /*useFrontdoorWorkflow*/,
        true /*startRecognitionInSpeechToTextClient*/,
        false /*isVoiceInterfaceVersion2Enabled*/
      );

      // We need to set the callback where all annotations will come to and forward them to our speech to text client.
      alRuntimeManager.setAnnotationResultCallback({
          handler: (op: IOperation) => {
            this.speechToTextClient.getAnnotationObserver().onAnnotationResult(op);
          }
      });

      await this.speechToTextClient.initializeAsync([SpeechToTextResultType.PartialResult, SpeechToTextResultType.FinalResult]);
    }

    private OnAudioBufferCallback(buffer: AudioBuffer) {
      this.onAudioBuffer(buffer);
    }
}
